"use client";

import { useState, useMemo, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  Video,
  Armchair,
} from "lucide-react";
import {
  useGetAvailableDatesQuery,
  useGetSlotsByDateQuery,
} from "@/redux/features/publicCalender/publicCalender";
import { ServiceTheme } from "./BookingStepper";
import TimezoneSelector, {
  useSelectedTimezone,
} from "@/components/v3/booking/TimezoneSelector";
import {
  BUSINESS_TIMEZONE,
  convertTimeToTimezone,
  formatTimeWithTimezone,
  getTimezoneAbbreviation,
} from "@/lib/timezone";

// Brand colors matching BookingStepper theme colors
const themePrimaryColors: Record<
  ServiceTheme,
  { primary: string; hover: string }
> = {
  counseling: { primary: "#896deb", hover: "#7a5de0" },
  lifeCoaching: { primary: "#DDA500", hover: "#c99400" },
  occupationalTherapy: { primary: "#C01610", hover: "#a01210" },
  psychology: { primary: "#C01610", hover: "#a01210" },
};

// Default blue theme (used when no service is selected yet)
const defaultThemePrimary = { primary: "#006DFF", hover: "#0059e6" };

type ThemeColor = { primary: string; hover: string };

const hexToRgba = (hex: string, alpha: number) => {
  const value = parseInt(hex.replace("#", ""), 16);
  const r = (value >> 16) & 255;
  const g = (value >> 8) & 255;
  const b = value & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export interface TimeSlotFirst {
  start_time: string;
  end_time: string;
  type: string;
  practitioner_count: number;
  // All unique time ranges that map to this normalized start time
  // Used for querying practitioners across different time formats
  all_time_ranges?: { start_time: string; end_time: string }[];
}

export interface DateTimeSelectionFirst {
  date: Date | null;
  startTime: string | null;
  endTime: string | null;
  sessionType: string;
  // All unique time ranges for querying practitioners across different time formats
  allTimeRanges?: { start_time: string; end_time: string }[];
}

interface BookingSummaryData {
  sessionType: "online" | "in-person";
  supportOption: string;
  duration: string;
  price: number;
}

interface DateTimePickerFirstProps {
  title?: string;
  subtitle?: string;
  bookingSummary?: BookingSummaryData;
  defaultSelection?: DateTimeSelectionFirst;
  onSelectionChange?: (selection: DateTimeSelectionFirst) => void;
  divisionId: string;
  serviceId?: string;
  sessionType?: string; // ONLINE or IN_PERSON
  serviceTheme?: ServiceTheme;
}

const DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Format time (24-hour or already-12-hour) to 12-hour AM/PM format
const formatTimeTo12Hour = (time: string): string => {
  if (!time) return "";
  // If the string already carries an AM/PM meridiem, preserve it as-is
  // instead of re-deriving it from the raw hour (which breaks PM times
  // like "4:30 PM" by misreading them as AM).
  const amPmMatch = time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
  if (amPmMatch) {
    const hours = parseInt(amPmMatch[1], 10);
    const minutes = amPmMatch[2];
    const meridiem = amPmMatch[3].toUpperCase();
    return `${hours}:${minutes} ${meridiem}`;
  }

  const match = time.match(/(\d{1,2}):(\d{2})/);
  if (!match) return time;
  const hours = parseInt(match[1]);
  const minutes = match[2];
  const ampm = hours >= 12 ? "PM" : "AM";
  const displayHour = hours > 12 ? hours - 12 : hours === 0 ? 12 : hours;
  return `${displayHour}:${minutes} ${ampm}`;
};

const getDaysInMonth = (year: number, month: number) => {
  return new Date(year, month + 1, 0).getDate();
};

const getFirstDayOfMonth = (year: number, month: number) => {
  const day = new Date(year, month, 1).getDay();
  return day === 0 ? 6 : day - 1;
};

const isSameDay = (date1: Date, date2: Date) => {
  return (
    date1.getDate() === date2.getDate() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getFullYear() === date2.getFullYear()
  );
};

// Calendar Component
const Calendar = ({
  selectedDate,
  onDateSelect,
  availableDates,
  isLoading,
  themeColor,
}: {
  selectedDate: Date | null;
  onDateSelect: (date: Date) => void;
  availableDates: Date[];
  isLoading?: boolean;
  themeColor: ThemeColor;
}) => {
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDayOfMonth = getFirstDayOfMonth(currentYear, currentMonth);

  const isDateAvailable = (date: Date) => {
    return availableDates.some((d) => isSameDay(d, date));
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const calendarDays = useMemo(() => {
    const days: (number | null)[] = [];
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    return days;
  }, [daysInMonth, firstDayOfMonth]);

  const weeks = useMemo(() => {
    const result: (number | null)[][] = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      result.push(calendarDays.slice(i, i + 7));
    }
    return result;
  }, [calendarDays]);

  if (isLoading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <div
          className="size-8 animate-spin rounded-full border-4"
          style={{
            borderColor: themeColor.primary,
            borderTopColor: "transparent",
          }}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 md:gap-6">
      <h3 className="font-onest text-xl font-semibold leading-none text-black md:text-[26px]">
        Select a Date & Time
      </h3>

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={handlePrevMonth}
          className="flex size-10 items-center justify-center rounded-full border transition-colors md:size-[50px]"
          style={{ borderColor: hexToRgba(themeColor.primary, 0.3) }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = hexToRgba(
              themeColor.primary,
              0.05,
            );
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
          aria-label="Previous month"
        >
          <ChevronLeft
            className="size-5 md:size-6"
            style={{ color: themeColor.primary }}
          />
        </button>
        <span className="font-onest text-base font-normal text-black md:text-xl">
          {MONTHS[currentMonth]} {currentYear}
        </span>
        <button
          type="button"
          onClick={handleNextMonth}
          className="flex size-10 items-center justify-center rounded-full border transition-colors md:size-[50px]"
          style={{
            borderColor: hexToRgba(themeColor.primary, 0.3),
            backgroundColor: themeColor.primary,
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = themeColor.hover;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = themeColor.primary;
          }}
          aria-label="Next month"
        >
          <ChevronRight className="size-5 text-white md:size-6" />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-2">
        {DAYS.map((day) => (
          <div
            key={day}
            className="py-2 text-center font-onest text-sm font-normal uppercase text-black"
          >
            {day}
          </div>
        ))}
      </div>

      <div className="flex flex-col gap-2">
        {weeks.map((week, weekIndex) => (
          <div key={weekIndex} className="grid grid-cols-7 gap-2">
            {week.map((day, dayIndex) => {
              if (day === null) {
                return (
                  <div key={dayIndex} className="size-10 md:size-[57px]" />
                );
              }

              const date = new Date(currentYear, currentMonth, day);
              const isSelected = !!(
                selectedDate && isSameDay(date, selectedDate)
              );
              const isAvailable = isDateAvailable(date);
              const isPast = date < new Date(today.setHours(0, 0, 0, 0));

              const isSelectable = isAvailable && !isPast;

              return (
                <button
                  key={dayIndex}
                  type="button"
                  onClick={() => isSelectable && onDateSelect(date)}
                  disabled={!isSelectable}
                  className={`flex size-10 items-center justify-center rounded-full font-onest text-base transition-all duration-200 md:size-[57px] md:text-xl ${isSelected ? "font-bold text-white" : isSelectable ? "font-bold" : "font-normal text-black/60"} ${isSelectable ? "cursor-pointer" : "cursor-not-allowed"} `}
                  style={
                    isSelected
                      ? { backgroundColor: themeColor.primary }
                      : isSelectable
                        ? {
                            backgroundColor: hexToRgba(themeColor.primary, 0.1),
                            color: themeColor.primary,
                          }
                        : undefined
                  }
                  onMouseEnter={(e) => {
                    if (isSelectable && !isSelected) {
                      e.currentTarget.style.backgroundColor = hexToRgba(
                        themeColor.primary,
                        0.2,
                      );
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (isSelectable && !isSelected) {
                      e.currentTarget.style.backgroundColor = hexToRgba(
                        themeColor.primary,
                        0.1,
                      );
                    }
                  }}
                  aria-label={`${day} ${MONTHS[currentMonth]} ${currentYear}`}
                  aria-pressed={isSelected}
                >
                  {day}
                </button>
              );
            })}
          </div>
        ))}
      </div>

      {/* Time Zone Selector */}
      <div className="pt-4">
        <TimezoneSelector themeColor={themeColor.primary} />
      </div>
    </div>
  );
};

// Time Slots Component
const TimeSlots = ({
  selectedDate,
  selectedTime,
  timeSlots,
  onTimeSelect,
  isLoading,
  themeColor,
  effectiveTimezone,
  isLocalTimezone,
}: {
  selectedDate: Date | null;
  selectedTime: string | null;
  timeSlots: TimeSlotFirst[];
  onTimeSelect: (
    startTime: string,
    endTime: string,
    allTimeRanges?: { start_time: string; end_time: string }[],
  ) => void;
  isLoading?: boolean;
  themeColor: ThemeColor;
  effectiveTimezone: string;
  isLocalTimezone: boolean;
}) => {
  if (!selectedDate) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <p className="text-center font-onest text-lg text-black/60">
          Select a date to view available times
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div
          className="size-8 animate-spin rounded-full border-4"
          style={{
            borderColor: themeColor.primary,
            borderTopColor: "transparent",
          }}
        />
      </div>
    );
  }

  const dayName = selectedDate.toLocaleDateString("en-US", { weekday: "long" });
  const monthName = selectedDate.toLocaleDateString("en-US", { month: "long" });
  const day = selectedDate.getDate();

  // Helper to format time in the selected timezone
  const formatSlotTime = (time: string): string => {
    if (!isLocalTimezone) {
      return formatTimeTo12Hour(time);
    }
    // Convert from business timezone to user's local timezone
    const dateStr = `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`;
    const convertedTime = convertTimeToTimezone(
      time,
      dateStr,
      BUSINESS_TIMEZONE,
      effectiveTimezone
    );
    return formatTimeTo12Hour(convertedTime);
  };

  const tzAbbrev = getTimezoneAbbreviation(effectiveTimezone);

  if (timeSlots.length === 0) {
    return (
      <div className="flex h-full w-full flex-col gap-6">
        <h3 className="shrink-0 font-onest text-xl font-normal text-black">
          {dayName}, {monthName} {day}
        </h3>
        <div className="flex flex-1 items-center justify-center">
          <p className="text-center font-onest text-lg text-black/60">
            No available times for this date
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col gap-6">
      <div className="flex shrink-0 flex-col">
        <h3 className="font-onest text-xl font-normal text-black">
          {dayName}, {monthName} {day}
        </h3>
        {isLocalTimezone && (
          <span className="font-onest text-xs text-black/50">
            Times shown in {tzAbbrev}
          </span>
        )}
      </div>

      <div className="flex flex-1 flex-col gap-2 overflow-y-auto pr-2">
        {timeSlots.map((slot) => {
          const isSelected = selectedTime === slot.start_time;
          return (
            <button
              key={`${slot.start_time}-${slot.end_time}`}
              type="button"
              onClick={() =>
                onTimeSelect(
                  slot.start_time,
                  slot.end_time,
                  slot.all_time_ranges,
                )
              }
              className="flex w-full shrink-0 flex-col items-center justify-center rounded-[10px] border px-7 py-3.5 font-onest transition-all duration-200"
              style={
                isSelected
                  ? {
                      borderColor: themeColor.primary,
                      backgroundColor: themeColor.primary,
                      color: "#fff",
                    }
                  : {
                      borderColor: hexToRgba(themeColor.primary, 0.6),
                      color: themeColor.primary,
                    }
              }
              onMouseEnter={(e) => {
                if (!isSelected)
                  e.currentTarget.style.backgroundColor = hexToRgba(
                    themeColor.primary,
                    0.05,
                  );
              }}
              onMouseLeave={(e) => {
                if (!isSelected)
                  e.currentTarget.style.backgroundColor = "transparent";
              }}
              aria-label={`Select time ${formatSlotTime(slot.start_time)}`}
              aria-pressed={isSelected}
            >
              <span className="text-lg font-bold">
                {formatSlotTime(slot.start_time)}
              </span>
              <span
                className="text-xs"
                style={{
                  color: isSelected
                    ? "rgba(255,255,255,0.8)"
                    : hexToRgba(themeColor.primary, 0.6),
                }}
              >
                {slot.practitioner_count} practitioner
                {slot.practitioner_count > 1 ? "s" : ""} available
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Booking Summary Component
const BookingSummary = ({
  summary,
  selectedDate,
  selectedTime,
  themeColor,
  effectiveTimezone,
  isLocalTimezone,
}: {
  summary: BookingSummaryData;
  selectedDate: Date | null;
  selectedTime: string | null;
  themeColor: ThemeColor;
  effectiveTimezone: string;
  isLocalTimezone: boolean;
}) => {
  const formatDate = (date: Date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const SessionIcon = summary.sessionType === "online" ? Video : Armchair;

  return (
    <div className="flex flex-col">
      <h3 className="p-2.5 font-onest text-2xl font-semibold text-black">
        Booking Summary
      </h3>

      <div className="p-2.5">
        <div
          className="inline-flex items-center gap-2.5 rounded-[20px] border-4 border-white px-5 py-2.5"
          style={{
            backgroundColor: themeColor.primary,
            boxShadow: "inset 0px 0px 20px 0px rgba(255,255,255,0.5)",
          }}
        >
          <SessionIcon className="size-6 text-white" />
          <span className="font-onest font-medium text-white">
            {summary.sessionType === "online"
              ? "Online Session"
              : "In-Person Session"}
          </span>
        </div>
      </div>

      <div className="p-2.5">
        <div
          className="border-l-2 pl-2.5"
          style={{ borderColor: themeColor.primary }}
        >
          <div className="p-2.5">
            <span className="font-onest text-base font-semibold text-black">
              {summary.supportOption}
            </span>
          </div>
          <div className="flex items-center gap-7 p-2.5">
            <div className="flex items-center gap-1.5">
              <Clock className="size-6" style={{ color: themeColor.primary }} />
              <span
                className="font-onest text-xl font-medium"
                style={{ color: themeColor.primary }}
              >
                {summary.duration}
              </span>
            </div>
            <span className="font-onest text-xl font-bold text-black/60">
              ${summary.price}
            </span>
          </div>
        </div>
      </div>

      <div
        className="rounded-[10px] p-2.5"
        style={{ backgroundColor: hexToRgba(themeColor.primary, 0.1) }}
      >
        <div className="p-2.5">
          <span className="font-onest text-base font-semibold text-black">
            Appointment
          </span>
        </div>
        <div className="flex gap-2.5 px-2.5">
          <span className="font-onest text-base font-normal text-black/60">
            {selectedDate ? formatDate(selectedDate) : "Select a date"}
          </span>
          <span className="font-onest text-base font-normal text-black/60">
            {selectedTime && selectedDate
              ? isLocalTimezone
                ? formatTimeTo12Hour(
                    convertTimeToTimezone(
                      selectedTime,
                      `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`,
                      BUSINESS_TIMEZONE,
                      effectiveTimezone
                    )
                  )
                : formatTimeTo12Hour(selectedTime)
              : "Select a time"}
          </span>
        </div>
        <div className="px-2.5 pb-2.5">
          <span className="font-onest text-xs font-normal text-black/30">
            {isLocalTimezone
              ? `Your time (${getTimezoneAbbreviation(effectiveTimezone)})`
              : `Australia/Sydney (${getTimezoneAbbreviation(BUSINESS_TIMEZONE)})`}
          </span>
        </div>
      </div>

      <div className="border-b border-black/20 p-2.5">
        <div className="p-2.5">
          <span className="font-onest text-base font-semibold text-black">
            Total Cost
          </span>
        </div>
        <div className="flex items-center justify-between p-2.5">
          <span className="font-onest text-xl font-medium text-black/60">
            Session Fee
          </span>
          <span className="font-onest text-xl font-normal text-black/60">
            ${summary.price} AUD
          </span>
        </div>
      </div>

      <div className="p-2.5">
        <div className="flex items-center justify-between p-2.5">
          <span className="font-onest text-xl font-medium text-black/60">
            Total
          </span>
          <span className="font-onest text-xl font-normal text-black/60">
            ${summary.price} AUD
          </span>
        </div>
      </div>
    </div>
  );
};

const defaultBookingSummary: BookingSummaryData = {
  sessionType: "online",
  supportOption: "Session",
  duration: "60 min",
  price: 130,
};

// Main Component
const DateTimePickerFirst = ({
  title = "Choose Date & Time",
  subtitle,
  bookingSummary = defaultBookingSummary,
  defaultSelection,
  onSelectionChange,
  divisionId,
  serviceId,
  sessionType = "ONLINE",
  serviceTheme,
}: DateTimePickerFirstProps) => {
  const themeColor = serviceTheme
    ? themePrimaryColors[serviceTheme]
    : defaultThemePrimary;
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    defaultSelection?.date || null,
  );
  const [selectedStartTime, setSelectedStartTime] = useState<string | null>(
    defaultSelection?.startTime || null,
  );
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedEndTime, setSelectedEndTime] = useState<string | null>(
    defaultSelection?.endTime || null,
  );

  // Get timezone state
  const { effectiveTimezone, isLocalTimezone } = useSelectedTimezone();

  // Dynamic subtitle based on timezone
  const displaySubtitle = useMemo(() => {
    if (subtitle) return subtitle;
    const tzAbbrev = getTimezoneAbbreviation(effectiveTimezone);
    if (isLocalTimezone) {
      return `Times shown in your local timezone (${tzAbbrev})`;
    }
    return `All times displayed in Australian Eastern Time (${tzAbbrev})`;
  }, [subtitle, effectiveTimezone, isLocalTimezone]);

  // Fetch available dates across all practitioners
  const { data: datesResponse, isLoading: datesLoading } =
    useGetAvailableDatesQuery(
      {
        division_id: divisionId,
        session_type: sessionType,
        service_id: serviceId,
      },
      { skip: !divisionId },
    );

  // Format selected date for slots query
  const formattedDate = selectedDate
    ? `${selectedDate.getFullYear()}-${String(selectedDate.getMonth() + 1).padStart(2, "0")}-${String(selectedDate.getDate()).padStart(2, "0")}`
    : "";

  // Fetch time slots for selected date
  const { data: slotsResponse, isLoading: slotsLoading } =
    useGetSlotsByDateQuery(
      {
        date: formattedDate,
        division_id: divisionId,
        session_type: sessionType,
        service_id: serviceId,
      },
      { skip: !divisionId || !selectedDate },
    );

  // Convert API dates to Date objects
  const availableDates: Date[] = useMemo(() => {
    if (datesResponse?.data?.dates) {
      return datesResponse.data.dates
        .filter(
          (d: { date: string; hasAvailability: boolean }) => d.hasAvailability,
        )
        .map((d: { date: string }) => {
          const [year, month, day] = d.date.split("-").map(Number);
          return new Date(year, month - 1, day);
        });
    }
    return [];
  }, [datesResponse]);

  // Normalize time to 24-hour HH:MM format for consistent comparison
  const normalizeTime = (time: string): string => {
    const match = time.match(/(\d{1,2}):(\d{2})\s*(AM|PM)?/i);
    if (!match) return time;
    let hours = parseInt(match[1]);
    const minutes = match[2];
    const meridiem = match[3]?.toUpperCase();

    // Convert to 24-hour format if AM/PM is present
    if (meridiem === "PM" && hours !== 12) {
      hours += 12;
    } else if (meridiem === "AM" && hours === 12) {
      hours = 0;
    }

    return `${hours.toString().padStart(2, "0")}:${minutes}`;
  };

  // Convert API slots to time slots and deduplicate by start_time
  const timeSlots: TimeSlotFirst[] = useMemo(() => {
    if (slotsResponse?.data?.slots) {
      // Deduplicate slots by normalized start_time
      // Store all unique time ranges to query practitioners correctly
      const uniqueSlots = new Map<
        string,
        TimeSlotFirst & {
          timeRanges: { start_time: string; end_time: string }[];
        }
      >();
      for (const slot of slotsResponse.data.slots) {
        const normalizedTime = normalizeTime(slot.start_time);
        const timeRange = {
          start_time: slot.start_time,
          end_time: slot.end_time,
        };

        if (!uniqueSlots.has(normalizedTime)) {
          uniqueSlots.set(normalizedTime, {
            ...slot,
            practitioner_count: slot.practitioner_count || 1,
            timeRanges: [timeRange],
          });
        } else {
          // If duplicate exists, add to time ranges and sum practitioner counts
          const existing = uniqueSlots.get(normalizedTime)!;
          // Check if this exact time range already exists
          const rangeExists = existing.timeRanges.some(
            (r) =>
              r.start_time === slot.start_time && r.end_time === slot.end_time,
          );
          if (!rangeExists) {
            existing.timeRanges.push(timeRange);
            existing.practitioner_count =
              (existing.practitioner_count || 0) +
              (slot.practitioner_count || 1);
          }
        }
      }
      // Convert to array and add all_time_ranges field
      return Array.from(uniqueSlots.values()).map((slot) => ({
        start_time: slot.start_time,
        end_time: slot.end_time,
        type: slot.type,
        practitioner_count: slot.practitioner_count,
        all_time_ranges: slot.timeRanges,
      }));
    }
    return [];
  }, [slotsResponse]);

  useEffect(() => {
    if (defaultSelection?.date) {
      setSelectedDate(defaultSelection.date);
    }
    if (defaultSelection?.startTime) {
      setSelectedStartTime(defaultSelection.startTime);
    }
    if (defaultSelection?.endTime) {
      setSelectedEndTime(defaultSelection.endTime);
    }
  }, [defaultSelection]);

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setSelectedStartTime(null);
    setSelectedEndTime(null);
    onSelectionChange?.({
      date,
      startTime: null,
      endTime: null,
      sessionType,
    });
  };

  const handleTimeSelect = (
    startTime: string,
    endTime: string,
    allTimeRanges?: { start_time: string; end_time: string }[],
  ) => {
    setSelectedStartTime(startTime);
    setSelectedEndTime(endTime);
    onSelectionChange?.({
      date: selectedDate,
      startTime,
      endTime,
      sessionType,
      allTimeRanges,
    });
  };

  return (
    <div className="flex flex-col gap-10">
      <div className="flex flex-col gap-2">
        <h2 className="font-crimsonText text-2xl font-bold leading-tight text-v3-secondary sm:text-3xl md:text-[42px] md:leading-none">
          {title}
        </h2>
        <p className="max-w-[500px] font-onest text-base font-normal leading-[1.2] text-v3-secondary md:text-lg md:leading-[1.1]">
          {displaySubtitle}
        </p>
      </div>

      <div className="flex flex-col gap-5 lg:flex-row lg:gap-5 2xl:gap-10">
        <div className="w-full rounded-[10px] bg-white lg:w-auto">
          <Calendar
            selectedDate={selectedDate}
            onDateSelect={handleDateSelect}
            availableDates={availableDates}
            isLoading={datesLoading}
            themeColor={themeColor}
          />
        </div>

        <div className="h-[400px] max-h-[400px] w-full overflow-hidden rounded-[10px] bg-white lg:h-[659px] lg:max-h-none lg:w-[260px] lg:shrink-0 2xl:w-[340px]">
          <TimeSlots
            selectedDate={selectedDate}
            selectedTime={selectedStartTime}
            timeSlots={timeSlots}
            onTimeSelect={handleTimeSelect}
            isLoading={slotsLoading}
            themeColor={themeColor}
            effectiveTimezone={effectiveTimezone}
            isLocalTimezone={isLocalTimezone}
          />
        </div>

        <div className="w-full rounded-[10px] bg-white lg:flex-1">
          <BookingSummary
            summary={bookingSummary}
            selectedDate={selectedDate}
            selectedTime={selectedStartTime}
            themeColor={themeColor}
            effectiveTimezone={effectiveTimezone}
            isLocalTimezone={isLocalTimezone}
          />
        </div>
      </div>
    </div>
  );
};

export default DateTimePickerFirst;
